import { MouseEvent } from 'react';

import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';

import { Box, Radio, SxProps, Theme, Typography } from '@mui/material';

interface Answer {
  key: string;
  value: string;
  isSelected: boolean;
}

export type QuestionRowProps = Readonly<{
  title: string;
  description: string;
  expanded: boolean;
  toggleExpand: () => void;
  answers: Answer[];
  onAnswerChange: (key: string) => void;
}>;

export const QuestionRow = ({
  title,
  description,
  expanded,
  toggleExpand,
  answers,
  onAnswerChange,
}: QuestionRowProps) => {
  const handleAnswerClick = (e: MouseEvent<HTMLElement>, answer: Answer) => {
    e.stopPropagation();

    if (!expanded) {
      // only allow answering when the item is expanded
      toggleExpand();

      return;
    }

    onAnswerChange(answer.key);
  };

  return (
    <Box onClick={toggleExpand} sx={containerSx}>
      <Box sx={titleOnlySx(expanded)}>
        <Typography
          variant="text-sm-medium"
          component="div"
          color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
        >
          {title}
        </Typography>
        <AnimatedCollapse isOpen={expanded} direction="vertical">
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {description}
          </Typography>
        </AnimatedCollapse>
      </Box>

      <Box sx={answerContainerSx}>
        {answers.map((answer) => (
          <Box
            key={answer.key}
            sx={answerItemSx(expanded, answers.length)}
            onClick={(e) => handleAnswerClick(e, answer)}
          >
            <Radio
              sx={radioSx}
              checked={answer.isSelected}
              onClick={(e) => handleAnswerClick(e, answer)}
              disableRipple={!expanded}
            />
            <AnimatedCollapse isOpen={expanded} direction="vertical">
              <Typography
                variant="text-xs-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              >
                {answer.value}
              </Typography>
            </AnimatedCollapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  cursor: 'pointer',
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const titleOnlySx =
  (expanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '25%',
    height: '100%',

    py: expanded ? backrs.spacing['spacing-6xl'] : backrs.spacing['spacing-3xl'],
    px: {
      xs: backrs.spacing['spacing-xl'],
      md: backrs.spacing['spacing-3xl'],
    },

    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${backrs.colors[expanded ? 'Colors/Base/Transparent' : 'Colors/Border/border-primary']}`,

    transition: 'all 0.3s ease-in-out',
  });

const answerContainerSx: SxProps<Theme> = {
  width: '75%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'all 0.3s ease-in-out',
};

const answerItemSx =
  (expanded: boolean, numAnswers: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `calc(100% / ${numAnswers})`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    px: backrs.spacing['spacing-3xl'],
    gap: expanded ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-none'],
    transition: 'all 0.3s ease-in-out',

    pt: expanded ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-3xl'],
    pb: expanded ? backrs.spacing['spacing-4xl'] : backrs.spacing['spacing-3xl'],
  });

const radioSx: SxProps<Theme> = {
  margin: 0,
  padding: 0,
};
